/**
 * Filtered card grid interactions.
 */

const SEARCH_DEBOUNCE_MS = 400;

function collectFilters(formEl) {
  if (!formEl) {
    return [];
  }

  const grouped = {};
  const checkboxes = formEl.querySelectorAll('.filtered-grid__checkbox:checked');

  checkboxes.forEach((input) => {
    const nameMatch = input.name.match(/^filters\[(.+?)\]\[\]$/);
    if (!nameMatch) {
      return;
    }

    const groupId = nameMatch[1];
    if (!grouped[groupId]) {
      grouped[groupId] = {
        type: input.dataset.filterType || 'taxonomy',
        metaKey: input.dataset.metaKey || groupId,
        values: [],
      };
    }

    grouped[groupId].values.push(String(input.value));
  });

  return Object.entries(grouped).map(([groupId, info]) => ({
    taxonomy: groupId,
    slugs: info.values,
    type: info.type,
    meta_key: info.metaKey,
  }));
}

async function fetchGrid({
  grid,
  resultsEl,
  cardType,
  context,
  nonce,
  perPage,
  constraintTaxonomy,
  constraintTermId,
  constraintMetaKey,
  constraintMetaValue,
  filters,
  search,
  paged,
  orderBy,
  order,
  randSeed,
}) {
  grid.classList.add('is-loading');

  try {
    const payload = new URLSearchParams();
    payload.set('action', 'mapo_filtered_grid');
    payload.set('nonce', nonce || '');
    payload.set('card_type', cardType || '');
    payload.set('context', context || '');
    payload.set('per_page', String(perPage || 9));
    payload.set('paged', String(paged || 1));
    payload.set('search', search || '');
    payload.set('filters', JSON.stringify(filters || []));
    payload.set('constraint_taxonomy', constraintTaxonomy || '');
    payload.set('constraint_term_id', String(constraintTermId || 0));
    payload.set('constraint_meta_key', constraintMetaKey || '');
    payload.set('constraint_meta_value', String(constraintMetaValue || 0));
    payload.set('orderby', orderBy || 'date');
    payload.set('order', order || 'DESC');
    payload.set('rand_seed', String(randSeed || 0));
    payload.set('lang', mapoScripts.lang || '');

    const response = await fetch(mapoScripts.ajaxUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: payload.toString(),
    });

    const data = await response.json();
    if (data?.success && data?.data?.html) {
      resultsEl.innerHTML = data.data.html;
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  } catch (_error) {
    // Keep current HTML if request fails.
  } finally {
    grid.classList.remove('is-loading');
  }
}

export function initFilteredGrid(root = document) {
  const grids = root.querySelectorAll('.js-filtered-grid');

  grids.forEach((grid) => {
    if (grid.dataset.initialized === '1') {
      return;
    }

    grid.dataset.initialized = '1';

    const toggleEl = grid.querySelector('.js-filtered-grid-toggle');
    const layoutEl = grid.querySelector('.js-filtered-grid-layout');
    const sidebarEl = grid.querySelector('.js-filtered-grid-sidebar');
    const closeEl = grid.querySelector('.js-filtered-grid-close');
    const formEl = grid.querySelector('.js-filtered-grid-form');
    const countEl = grid.querySelector('.js-filtered-grid-count');
    const resetEl = grid.querySelector('.js-filtered-grid-reset');
    const searchEl = grid.querySelector('.js-filtered-grid-search');
    const resultsEl = grid.querySelector('.js-filtered-grid-results');

    const cardType = grid.dataset.cardType || 'esperienza';
    const context = grid.dataset.context || '';
    const nonce = grid.dataset.nonce || '';
    const perPage = Number.parseInt(grid.dataset.perPage || '9', 10);
    const constraintTaxonomy = grid.dataset.constraintTaxonomy || '';
    const constraintTermId = Number.parseInt(grid.dataset.constraintTermId || '0', 10);
    const constraintMetaKey = grid.dataset.constraintMetaKey || '';
    const constraintMetaValue = Number.parseInt(grid.dataset.constraintMetaValue || '0', 10);
    const orderBy = grid.dataset.orderBy || 'date';
    const order = grid.dataset.order || 'DESC';
    const randSeed = Number.parseInt(grid.dataset.randSeed || '0', 10);

    let filters = collectFilters(formEl);
    let search = (searchEl?.value || '').trim();
    let searchTimer = null;

    const request = (page = 1) => fetchGrid({
      grid,
      resultsEl,
      cardType,
      context,
      nonce,
      perPage,
      constraintTaxonomy,
      constraintTermId,
      constraintMetaKey,
      constraintMetaValue,
      filters,
      search,
      paged: page,
      orderBy,
      order,
      randSeed,
    });

    const hasAnyAppliedFilters = () => filters.some((group) => Array.isArray(group.slugs) && group.slugs.length > 0);

    const getSelectedFilterCount = () => {
      if (!formEl) {
        return 0;
      }

      return formEl.querySelectorAll('.filtered-grid__checkbox:checked').length;
    };

    const updateFilterCount = () => {
      if (!countEl) {
        return;
      }

      const selectedFilterCount = getSelectedFilterCount();
      countEl.textContent = String(selectedFilterCount);
      countEl.hidden = selectedFilterCount < 1;
    };

    const updateResetVisibility = () => {
      if (!formEl || !resetEl) {
        return;
      }

      resetEl.hidden = !hasAnyAppliedFilters();
    };

    const closeFiltersPanel = ({ focusToggle = false } = {}) => {
      if (!layoutEl || !sidebarEl || !toggleEl) {
        return;
      }

      layoutEl.classList.remove('is-filters-open');
      sidebarEl.setAttribute('aria-hidden', 'true');
      toggleEl.setAttribute('aria-expanded', 'false');

      if (focusToggle) {
        toggleEl.focus();
      }
    };

    if (layoutEl && sidebarEl && toggleEl) {
      closeFiltersPanel();
    }

    if (toggleEl && layoutEl && sidebarEl) {
      toggleEl.addEventListener('click', () => {
        const open = !layoutEl.classList.contains('is-filters-open');
        layoutEl.classList.toggle('is-filters-open', open);
        toggleEl.setAttribute('aria-expanded', String(open));
        sidebarEl.setAttribute('aria-hidden', String(!open));
      });
    }

    if (closeEl && layoutEl && sidebarEl && toggleEl) {
      closeEl.addEventListener('click', () => {
        closeFiltersPanel({ focusToggle: true });
      });
    }

    const accordionToggles = grid.querySelectorAll('.js-filtered-grid-accordion-toggle');
    accordionToggles.forEach((buttonEl) => {
      const accordionEl = buttonEl.closest('.js-filtered-grid-accordion');
      const bodyEl = accordionEl?.querySelector('.filtered-grid__accordion-body');
      if (!accordionEl || !bodyEl) {
        return;
      }

      buttonEl.addEventListener('click', () => {
        const expanded = buttonEl.getAttribute('aria-expanded') === 'true';
        buttonEl.setAttribute('aria-expanded', String(!expanded));
        bodyEl.hidden = expanded;
        accordionEl.classList.toggle('is-open', !expanded);
      });
    });

    if (formEl) {
      formEl.addEventListener('change', () => {
        updateFilterCount();
      });

      formEl.addEventListener('submit', (event) => {
        event.preventDefault();
        filters = collectFilters(formEl);
        search = (searchEl?.value || '').trim();
        updateFilterCount();
        updateResetVisibility();
        request(1);
        closeFiltersPanel();
      });
    }

    if (resetEl && formEl) {
      resetEl.addEventListener('click', () => {
        formEl.querySelectorAll('.filtered-grid__checkbox:checked').forEach((checkboxEl) => {
          checkboxEl.checked = false;
        });

        filters = [];
        updateFilterCount();
        updateResetVisibility();
        request(1);
        closeFiltersPanel();
      });
    }

    if (searchEl) {
      searchEl.addEventListener('input', () => {
        if (searchTimer) {
          window.clearTimeout(searchTimer);
        }

        searchTimer = window.setTimeout(() => {
          search = searchEl.value.trim();
          filters = collectFilters(formEl);
          request(1);
        }, SEARCH_DEBOUNCE_MS);
      });
    }

    if (resultsEl) {
      resultsEl.addEventListener('click', (event) => {
        const linkEl = event.target.closest('.js-inline-pagination-link');
        if (!linkEl || !resultsEl.contains(linkEl)) {
          return;
        }

        event.preventDefault();

        const page = Number.parseInt(linkEl.dataset.page || '1', 10);
        if (!page) {
          return;
        }

        request(page);
      });
    }

    if (resetEl) {
      resetEl.hidden = true;
    }

    updateFilterCount();
  });
}
